import React from 'react';
import { useState, useCallback, useMemo } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
  Chip,
  styled,
  alpha,
  Collapse,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  boxShadow: 'none',
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: `${theme.spacing(1)} !important`,
  marginBottom: theme.spacing(1),
  '&:before': {
    display: 'none',
  },
  '&.Mui-expanded': {
    margin: `0 0 ${theme.spacing(1)} 0`,
  },
}));

const StyledAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.primary.main, 0.02),
  borderRadius: theme.spacing(1),
  minHeight: 30,
  '&.Mui-expanded': {
    minHeight: 30,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
  },
  '& .MuiAccordionSummary-content': {
    margin: '12px 0 !important',
    alignItems: 'center',
  },
  '& .MuiAccordionSummary-expandIconWrapper': {
    transition: 'transform 0.1s ease-in-out',
  },
}));

const StyledAccordionDetails = styled(AccordionDetails)(({ theme }) => ({
  borderRadius: 4,
  padding: 0,
  backgroundColor: theme.palette.background.paper,
}));

const SelectableListItem = styled(ListItem, {
  shouldForwardProp: prop => prop !== 'selected' && prop !== 'level',
})<{ selected?: boolean; level?: number }>(({ theme, selected, level = 0 }) => ({
  borderRadius: theme.spacing(0.75),
  marginBottom: theme.spacing(0.5),
  marginLeft: theme.spacing(level * 2),
  marginRight: theme.spacing(1),
  cursor: 'pointer',
  transition: 'all 0.1s ease-in-out',
  backgroundColor: selected ? theme.palette.Primary.main : 'transparent',
  color: selected ? theme.palette.primary.contrastText : theme.palette.text.primary,
  '&:hover': {
    backgroundColor: selected
      ? theme.palette.Primary.dark
      : alpha(theme.palette.Primary.main, 0.08),
    transform: 'translateX(2px)',
  },
  '& .MuiListItemIcon-root': {
    color: selected ? theme.palette.primary.contrastText : theme.palette.text.secondary,
    minWidth: 36,
  },
  '& .MuiListItemText-primary': {
    fontWeight: selected ? 600 : 400,
  },
}));

const NestedItemContainer = styled(Box)(({ theme }) => ({
  borderLeft: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
  marginLeft: theme.spacing(2),
  paddingLeft: theme.spacing(1),
}));

export interface BaseAccordionItem {
  id: string;
  label: string | React.ReactNode;
  icon?: React.ComponentType<any>;
  badge?: number | string;
  description?: string;
  disabled?: boolean;
  hidden?: boolean;
  data?: any;
  children?: BaseAccordionItem[];
  value: string;
}

export interface AccordionSection {
  id: string;
  title: string | React.ReactNode;
  items: BaseAccordionItem[];
  defaultExpanded?: boolean;
  icon?: React.ComponentType<any>;
  badge?: number | string;
  collapsible?: boolean;
}

export interface NestedAccordionProps {
  sections: AccordionSection[];
  selectedItems?: string[];
  onItemClick?: (item: BaseAccordionItem, path: string[]) => void;
  onSectionToggle?: (sectionId: string, expanded: boolean) => void;
  multiSelect?: boolean;
  showBadges?: boolean;
  showIcons?: boolean;
  maxNestingLevel?: number;
  className?: string;
  expandedSections?: string[];
  renderCustomLabel?: (item: BaseAccordionItem, level: number) => React.ReactNode;
  renderCustomBadge?: (badge: number | string) => React.ReactNode;
  keyToCompare?: string;
}

const AccordionItemRenderer: React.FC<{
  item: BaseAccordionItem;
  level: number;
  path: string[];
  selectedItems: string[];
  onItemClick: (item: BaseAccordionItem, path: string[]) => void;
  showIcons: boolean;
  showBadges: boolean;
  maxNestingLevel: number;
  renderCustomLabel?: (item: BaseAccordionItem, level: number) => React.ReactNode;
  renderCustomBadge?: (badge: number | string) => React.ReactNode;
  keyToCompare: string;
}> = ({
  item,
  level,
  path,
  selectedItems,
  onItemClick,
  showIcons,
  showBadges,
  maxNestingLevel,
  renderCustomLabel,
  renderCustomBadge,
  keyToCompare,
}) => {
  const [expanded, setExpanded] = useState(false);
  const isSelected = selectedItems.includes(item[keyToCompare]);
  const hasChildren = item.children && item.children.length > 0;
  const currentPath = [...path, item[keyToCompare]];

  const handleItemClick = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      if (!item.disabled) {
        onItemClick(item, path);
      }
    },
    [item, path, onItemClick]
  );

  const handleExpandClick = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      setExpanded(!expanded);
    },
    [expanded]
  );

  const renderLabel = () => {
    if (renderCustomLabel) {
      return renderCustomLabel(item, level);
    }

    if (React.isValidElement(item.label)) {
      return item.label;
    }

    return (
      <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
        <Typography variant="body1" component="span">
          {item.label}
        </Typography>
        {showBadges && item.badge && (
          <Box ml={1}>
            {renderCustomBadge ? (
              renderCustomBadge(item.badge)
            ) : (
              <Chip
                label={item.badge}
                size="small"
                variant={isSelected ? 'filled' : 'outlined'}
                sx={{
                  height: 20,
                  fontSize: '0.75rem',
                  backgroundColor: isSelected ? alpha('primary.contrastText', 0.2) : undefined,
                }}
              />
            )}
          </Box>
        )}
      </Box>
    );
  };

  if (item.hidden) {
    return null;
  }

  return (
    <Box>
      <SelectableListItem
        selected={isSelected}
        level={level}
        onClick={handleItemClick}
        disabled={item.disabled}
        title={typeof item.description === 'string' ? item.description : undefined}
      >
        {showIcons && item.icon && (
          <ListItemIcon>
            <item.icon />
          </ListItemIcon>
        )}
        <ListItemText primary={renderLabel()} />
        {hasChildren && level < maxNestingLevel && (
          <Box
            onClick={handleExpandClick}
            sx={{
              p: 0.5,
              borderRadius: 0.5,
              '&:hover': {
                backgroundColor: alpha('primary.main', 0.1),
              },
            }}
          >
            <ChevronRightIcon
              sx={{
                transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
                transition: 'transform 0.1s ease-in-out',
                fontSize: 20,
              }}
            />
          </Box>
        )}
      </SelectableListItem>

      {hasChildren && level < maxNestingLevel && (
        <Collapse in={expanded}>
          <NestedItemContainer>
            {item.children!.map(childItem => (
              <AccordionItemRenderer
                key={childItem[keyToCompare]}
                item={childItem}
                level={level + 1}
                path={currentPath}
                selectedItems={selectedItems}
                onItemClick={onItemClick}
                showIcons={showIcons}
                showBadges={showBadges}
                maxNestingLevel={maxNestingLevel}
                renderCustomLabel={renderCustomLabel}
                renderCustomBadge={renderCustomBadge}
                keyToCompare={keyToCompare}
              />
            ))}
          </NestedItemContainer>
        </Collapse>
      )}
    </Box>
  );
};

export const NestedAccordion: React.FC<NestedAccordionProps> = ({
  sections,
  selectedItems = [],
  onItemClick,
  onSectionToggle,
  multiSelect = false,
  showBadges = true,
  showIcons = true,
  maxNestingLevel = 3,
  className,
  expandedSections = [],
  renderCustomLabel,
  renderCustomBadge,
  keyToCompare = 'id',
}) => {
  const [internalExpandedSections, setInternalExpandedSections] = useState<string[]>(
    sections.filter(section => section.defaultExpanded).map(section => section.id)
  );

  const currentExpandedSections =
    expandedSections.length > 0 ? expandedSections : internalExpandedSections;

  const handleSectionToggle = useCallback(
    (sectionId: string) => {
      const isExpanded = currentExpandedSections.includes(sectionId);
      const newExpanded = isExpanded
        ? currentExpandedSections.filter(id => id !== sectionId)
        : [...currentExpandedSections, sectionId];

      if (expandedSections.length === 0) {
        setInternalExpandedSections(newExpanded);
      }

      onSectionToggle?.(sectionId, !isExpanded);
    },
    [currentExpandedSections, expandedSections, onSectionToggle]
  );

  const handleItemClick = useCallback(
    (item: BaseAccordionItem, path: string[]) => {
      onItemClick?.(item, path);
    },
    [onItemClick]
  );

  const renderSectionTitle = (section: AccordionSection) => {
    if (React.isValidElement(section.title)) {
      return section.title;
    }

    return (
      <Box display="flex" alignItems="center" gap={1}>
        {section.icon && (
          <ListItemIcon sx={{minWidth: '28px'}}>
            <section.icon />
          </ListItemIcon>
        )}
        <Typography variant="body1" component="span">
          {section.title}
        </Typography>
        {showBadges && section.badge && (
          <Chip
            label={section.badge}
            size="small"
            color="primary"
            variant="outlined"
            sx={{ height: 20, fontSize: '0.75rem' }}
          />
        )}
      </Box>
    );
  };

  const visibleSections = useMemo(
    () => sections.filter(section => section.items.length > 0),
    [sections]
  );

  return (
    <Box className={className}>
      {visibleSections.map(section => {
        const isExpanded = currentExpandedSections.includes(section.id);

        if (section.collapsible === false) {
          return (
            <Box key={section.id} mb={2}>
              <Box>{renderSectionTitle(section)}</Box>
              <List sx={{ py: 0 }}>
                {section.items.map(item => (
                  <AccordionItemRenderer
                    key={item[keyToCompare]}
                    item={item}
                    level={0}
                    path={[section.id]}
                    selectedItems={selectedItems}
                    onItemClick={handleItemClick}
                    showIcons={showIcons}
                    showBadges={showBadges}
                    maxNestingLevel={maxNestingLevel}
                    renderCustomLabel={renderCustomLabel}
                    renderCustomBadge={renderCustomBadge}
                    keyToCompare={keyToCompare}
                  />
                ))}
              </List>
            </Box>
          );
        }

        return (
          <StyledAccordion
            key={section.id}
            expanded={isExpanded}
            onChange={() => handleSectionToggle(section.id)}
          >
            <StyledAccordionSummary expandIcon={<ExpandMoreIcon />}>
              {renderSectionTitle(section)}
            </StyledAccordionSummary>
            <StyledAccordionDetails>
              <List sx={{ py: 1 }}>
                {section.items.map(item => (
                  <AccordionItemRenderer
                    key={item[keyToCompare]}
                    item={item}
                    level={0}
                    path={[section.id]}
                    selectedItems={selectedItems}
                    onItemClick={handleItemClick}
                    showIcons={showIcons}
                    showBadges={showBadges}
                    maxNestingLevel={maxNestingLevel}
                    renderCustomLabel={renderCustomLabel}
                    renderCustomBadge={renderCustomBadge}
                    keyToCompare={keyToCompare}
                  />
                ))}
              </List>
            </StyledAccordionDetails>
          </StyledAccordion>
        );
      })}
    </Box>
  );
};

export const createAccordionItem = (
  id: string,
  label: string | React.ReactNode,
  options: Partial<Omit<BaseAccordionItem, 'id' | 'label'>> = {}
): BaseAccordionItem => ({
  id,
  label,
  ...options,
});

export const createAccordionSection = (
  id: string,
  title: string | React.ReactNode,
  items: BaseAccordionItem[],
  options: Partial<Omit<AccordionSection, 'id' | 'title' | 'items'>> = {}
): AccordionSection => ({
  id,
  title,
  items,
  ...options,
});

export default NestedAccordion;
